import type { ReactNode } from 'react';
import { LinkChip } from './LinkChip';
import { splitLinks } from './links';
import { mentionRegex } from './mentions';

/** Plain text with web addresses drawn as Roger chips or new-tab links. */
function pushWithLinks(
  parts: ReactNode[],
  text: string,
  key: number,
  isOwn: boolean
) {
  splitLinks(text).forEach((part, i) => {
    const k = `${key}-${i}`;
    if (part.type === 'text') parts.push(part.text);
    else if (part.roger)
      parts.push(<LinkChip key={k} url={part.url} isOwn={isOwn} />);
    else
      parts.push(
        <a
          key={k}
          href={part.url}
          target='_blank'
          rel='noopener noreferrer'
          onClick={e => e.stopPropagation()}
          className='underline'
        >
          {part.url}
        </a>
      );
  });
}

/** A message body with each mention token drawn as a highlighted `@Name`. */
export function MessageText({
  body,
  currentUserId,
  isOwn,
}: {
  body: string;
  currentUserId?: string;
  isOwn: boolean;
}) {
  const readerId = currentUserId?.toLowerCase();
  const parts: ReactNode[] = [];
  const addText = (text: string, key: number) =>
    pushWithLinks(parts, text, key, isOwn);
  let last = 0;
  for (const match of body.matchAll(mentionRegex())) {
    const [token, id, label] = match;
    const start = match.index ?? 0;
    if (start > last) addText(body.slice(last, start), last);
    const ofReader = !isOwn && !!readerId && id === readerId;
    const tint = isOwn
      ? 'bg-white/20'
      : ofReader
        ? 'bg-amber-100 text-amber-900'
        : 'bg-primary/10';
    parts.push(
      <span key={start} className={`rounded px-0.5 font-semibold ${tint}`}>
        @{label}
        {ofReader && <span className='sr-only'>, mentions you</span>}
      </span>
    );
    last = start + token.length;
  }
  if (last < body.length) addText(body.slice(last), last);
  return <>{parts}</>;
}
